import { chart as rawChart, getTypeName } from '@rawgraphs/rawgraphs-core'
import { buildDimensionMappingForColumn } from '../services/chartDimensionMapping'
import { normalizeChartMapping } from '../services/normalizeChartMapping'

function getFields(dataTypes, rows) {
  return Object.keys(dataTypes || {}).map((name) => ({
    name,
    type: getTypeName(dataTypes[name]),
    uniqueValues: new Set((rows || []).slice(0, 2000).map((row) => row[name])).size,
  }))
}

function scoreField(field, dimension) {
  const dimensionName = `${dimension.id} ${dimension.name || ''}`.toLowerCase()
  const fieldName = field.name.toLowerCase()
  let score = 0

  if (/lat/.test(dimensionName)) score += /lat/.test(fieldName) ? 50 : -50
  if (/(lon|long)/.test(dimensionName)) score += /(lon|long)/.test(fieldName) ? 50 : -50
  if (/(date|time|year|start|end|x)/.test(dimensionName)) score += field.type === 'date' ? 25 : 0
  if (/(size|value|amount|count|weight|height|radius|y)/.test(dimensionName)) score += field.type === 'number' ? 25 : 0
  if (/(source|target|group|category|label|name|hierarchy|series|line|color|bar|step)/.test(dimensionName)) {
    score += field.type === 'string' ? 20 : 0
    score += field.uniqueValues > 1 && field.uniqueValues <= 40 ? 10 : 0
  }
  return score
}

function compatibleFields(fields, dimension) {
  return fields
    .filter((field) => !dimension.validTypes?.length || dimension.validTypes.includes(field.type))
    .sort((a, b) => scoreField(b, dimension) - scoreField(a, dimension))
}

// The Bumpchart metadata calls Size optional because RAWGraphs supports a
// handful of advanced configurations, but its mapper still aggregates Size
// for every rendered point. In Canvas, where the compact mapper exposes the
// standard configuration only, a numeric Size field is essential.
export function getCanvasRequiredDimensions(chart) {
  const required = (chart?.dimensions || []).filter((dimension) => dimension.required)
  if (chart?.metadata?.id !== 'rawgraphs.bumpchart') return required
  const size = chart.dimensions.find((dimension) => dimension.id === 'size')
  return size && !required.includes(size) ? [...required, size] : required
}

/**
 * Build a conservative first-pass mapping for every required chart dimension.
 * It deliberately leaves optional dimensions empty: the board is for seeing
 * viable chart families, not attempting an opaque "perfect" interpretation.
 */
export function buildCompatibleChartMapping(chart, dataTypes, rows) {
  const fields = getFields(dataTypes, rows)
  const mapping = {}
  const usedForDistinctRoles = new Set()

  for (const dimension of getCanvasRequiredDimensions(chart)) {
    const candidates = compatibleFields(fields, dimension)
    const preferDistinct = /source|target|from|to/i.test(`${dimension.id} ${dimension.name || ''}`)
    const available = preferDistinct
      ? candidates.filter((field) => !usedForDistinctRoles.has(field.name)).concat(candidates)
      : candidates
    const selected = dimension.multiple ? available.slice(0, 2) : available.slice(0, 1)
    if (!selected.length) return null

    mapping[dimension.id] = selected.reduce(
      (current, field) => buildDimensionMappingForColumn(current, dimension, field.name, dataTypes),
      {}
    )
    if (preferDistinct) selected.forEach((field) => usedForDistinctRoles.add(field.name))
  }

  return Object.keys(mapping).length ? mapping : null
}

/**
 * Type compatibility alone is not enough: RAWGraphs mapping functions can
 * still return no marks for a particular chart/data combination. Preflight the
 * same mapper used by ChartPreview before adding a starter to the board.
 */
export function canPrepareCanvasChart(chart, mapping, rows, dataTypes) {
  try {
    const normalizedMapping = normalizeChartMapping(chart, mapping, dataTypes)
    const viz = rawChart(chart, { data: rows, mapping: normalizedMapping, dataTypes })
    const mappedData = viz._getVizData()
    return Array.isArray(mappedData) ? mappedData.length > 0 : Boolean(mappedData)
  } catch {
    return false
  }
}

/**
 * One source of truth for Canvas chart availability. Both the generator and
 * inspector use this list so a selectable chart is also one Canvas can map
 * and render for the active dataset.
 */
export function getCompatibleCanvasCharts(charts, dataTypes, rows, limit = Infinity) {
  return (charts || [])
    .map((chart) => ({
      chart,
      mapping: buildCompatibleChartMapping(chart, dataTypes, rows),
    }))
    .filter(({ chart, mapping }) => (
      Boolean(mapping) && canPrepareCanvasChart(chart, mapping, rows, dataTypes)
    ))
    .slice(0, limit)
}
